import React from 'react'
import { BarChart, Bar, Cell, ReferenceLine, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import styled from 'styled-components'
import { UIStyles, surfaces } from '../../theme'
import { Column, BoxContainer, ChartTitle, ChartSubTitle } from './Layout'
import themeColors from '@layers-frontend/commons/styles/themeColors'

import get from 'lodash/get'
import isNumber from 'lodash/isNumber'
import isInteger from 'lodash/isInteger'
import isEmpty from 'lodash/isEmpty'
import orderBy from 'lodash/orderBy'

// Chart chrome stays in the background so the data reads first: axes and their ticks at white 40% with soft white
// numbers, and a faint dashed grid with no fill.
export const chartAxisProps = { stroke: UIStyles.lightGrey, tick: { fill: UIStyles.whiteTransparent06 } }
export const chartGridProps = { strokeDasharray: '3 3', stroke: UIStyles.whiteTransparent01 }

// Chart tooltips look like the app's tooltips: deep navy, mint contour, 8px corners and the floating shadow.
export const TooltopHeading = styled.p`
  margin: 0px;
  padding: 8px 10px;
  background: ${themeColors.deepblueColor3};
  border: 1px solid ${themeColors.mintBorder};
  white-space: nowrap;
  border-radius: 8px;
  box-shadow: ${surfaces.floatingPanelShadow};
  color: ${themeColors.whiteColor};
  font-weight: bold;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  text-align: ${props => props.textAlign || 'center'};
`
export const TooltipWrapper = styled.div`
  margin: 0px;
  padding: 8px 10px;
  background: ${themeColors.deepblueColor3};
  border: 1px solid ${themeColors.mintBorder};
  white-space: nowrap;
  border-radius: 8px;
  box-shadow: ${surfaces.floatingPanelShadow};
  color: ${themeColors.whiteTransparent08};
  text-align: ${props => props.textAlign || 'center'};

  /* Under a heading only the bottom corners round. */
  &:not(:first-child) {
    border-top-right-radius: 0;
    border-top-left-radius: 0;
  }

  p.value {
    color: ${UIStyles.mintColor};
  }

  p:last-child {
    margin-bottom: 0;
  }
`

export const CustomTooltip = props => {
  if (!props.active) return null

  const { label, title, valueName, value } = props
  const formattedValue = isNumber(value) && !isInteger(value) ? +value.toFixed(2) : value

  return (
    <div>
      {title ? <TooltopHeading>{title}</TooltopHeading> : null}
      <TooltipWrapper>
        <p>{label}</p>
        <p className="value">
          {valueName}: {formattedValue}
        </p>
      </TooltipWrapper>
    </div>
  )
}

export const HistogramChartCustomTooltip = props => {
  if (!props.active) return null

  const customerName = get(props, 'payload[0].payload.customer')
  const label = get(props, 'payload[0].payload.label')
  const valueName = get(props, 'payload[0].name')
  const value = get(props, 'payload[0].value')
  const formattedValue = isNumber(value) && !isInteger(value) ? +value.toFixed(4) : value

  return (
    <div>
      {customerName ? <TooltopHeading>{customerName}</TooltopHeading> : null}
      <TooltipWrapper>
        <p>{label}</p>
        <p className="value">
          {valueName}: {formattedValue}
        </p>
      </TooltipWrapper>
    </div>
  )
}

// The bars open the field, so they brighten under the pointer.
const HistogramCard = styled(BoxContainer)`
  .recharts-bar-rectangle path {
    transition: fill-opacity 0.15s;
  }
  .recharts-bar-rectangle:hover path {
    fill-opacity: 0.85;
  }
`

const TitleAndAverageWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`
const AverageWrapper = styled.div`
  ${ChartSubTitle} {
    margin-bottom: 0px;
  }
  ${ChartSubTitle}:first-child {
    color: ${UIStyles.whiteTransparent06};
  }
  ${ChartSubTitle}:last-child {
    color: ${UIStyles.whiteColor};
  }
`

export const HistogramChart = ({ title, subtitle, data = [], XLabel, YLabel, withAverage, onClick, columnWidth, order = 'asc' }) => {
  if (isEmpty(data)) return null
  const average = withAverage ? data.reduce((sum, item) => sum + item[YLabel], 0) / data.length : null
  // eslint-disable-next-line no-extra-boolean-cast
  const formattedAverage = !!average ? (Math.abs(average) < 1 ? average.toFixed(4) : average.toFixed(2)) : ''
  const sortedData = orderBy(data, item => new Date(get(item, 'flight_date')), [order])
  return (
    <Column marginBottom="30px" width={columnWidth}>
      <HistogramCard height="auto">
        <TitleAndAverageWrapper>
          <ChartTitle>{title}</ChartTitle>
          <AverageWrapper>
            <ChartSubTitle>{subtitle}</ChartSubTitle>
            <ChartSubTitle>{formattedAverage}</ChartSubTitle>
          </AverageWrapper>
        </TitleAndAverageWrapper>
        <ResponsiveContainer width="100%" height={270}>
          <BarChart data={sortedData} margin={{ bottom: -15 }}>
            <CartesianGrid horizontal={false} vertical={false} strokeDasharray="3 3" />
            <XAxis strokeWidth="2" axisLine={false} dataKey={XLabel} tick={null} />
            <YAxis {...chartAxisProps} tickFormatter={value => `${value} `} tick={{ ...chartAxisProps.tick, dx: -5 }} />
            <Tooltip content={<HistogramChartCustomTooltip />} cursor={{ fill: UIStyles.whiteTransparent01 }} />
            <Bar dataKey={YLabel} fill={UIStyles.mintColor} fillOpacity={0.6} onClick={onClick} isAnimationActive={false}>
              {sortedData.map((_, index) => (
                <Cell cursor="pointer" key={`cell-${index}`} />
              ))}
            </Bar>
            {average ? <ReferenceLine y={average} stroke={UIStyles.whiteTransparent08} strokeWidth="1.5" strokeDasharray="5 5" /> : null}
            <ReferenceLine y={0} stroke={UIStyles.lightGrey} strokeWidth="1" />
          </BarChart>
        </ResponsiveContainer>
      </HistogramCard>
    </Column>
  )
}
